import { Box, Card, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import mqtt from 'mqtt'
import { useEffect, useRef, useState } from 'react'
import Chat from '~/components/Chat'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/automotive/bot')({ component: AutomotiveUnsBot })

const hivemqws = import.meta.env.VITE_SHARED_WS_ADDRESS

interface TopicNode {
  name: string
  attributes: {
    index: number
    value?: string
  }
  children?: TopicNode[]
}

const topicsRoot: TopicNode = {
  name: 'Root',
  attributes: {
    index: 0,
  },
  children: [],
}

const uns_options: mqtt.IClientOptions = {
  protocol: 'wss',
  port: 443,
  keepalive: 20,
  clientId: `stories_uns_automotive_bot_${Math.random().toString(16).substr(2, 8)}`,
  username: import.meta.env.VITE_WS_USERNAME,
  password: import.meta.env.VITE_WS_PASSWORD,
}

let uns_client: mqtt.MqttClient | null = null

function AutomotiveUnsBot() {
  const promptinit =
    'You are a analytic expert that manage an automotive unified namespace with a lot of informations on a different connected cars, with informations on motor, interior sensors, collision sensors. Here are important informations on each unit that you will find in the dataset, temperature are in celsius, humidity in %, pressure is defined in Psi. Here is a the dataset that you need to analyse :'
  const introMessage =
    "Hi, I'm AutoBot, feel free to ask me anything about the data in your Automotive Unified Namespace."
  const questionList = [
    'Is there any collisions on my vehicules ?',
    'Can you give me the average tank level ?',
    'Can you give me the lowest tank level and the identifier of the vehicule ?',
    'What is the average motor rpms on my vehicules ?',
    'What is the average fuel consumption on my vehicules ?',
    'Can you give me the location of the vehicule with identifier VIN-3JJ8FQ7H321 ?',
  ]

  const [topics, setTopics] = useState<TopicNode>(topicsRoot)
  const [isConnected, setIsConnected] = useState(false)
  const [messagesPerSecond, setMessagesPerSecond] = useState(0)
  const messageCountRef = useRef(0)
  const lastCountRef = useRef(0)
  const mpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function connect_mqtt(broker: string) {
    uns_client?.end()
    uns_client = mqtt.connect(`wss://${broker}:443/mqtt`, uns_options)

    uns_client.on('message', (topic, message) => {
      messageCountRef.current++
      updateTreeWithNewNode(topic.toString(), message.toString())
    })

    uns_client.on('connect', () => {
      console.log(`Connected to ${broker}`)
      setIsConnected(true)
      uns_client?.subscribe('ConnCorp/#', { qos: 1 }, (err) => {
        if (err) {
          console.log(err)
        }
      })
    })

    uns_client.on('close', () => {
      setIsConnected(false)
    })

    uns_client.on('error', (err) => {
      console.log('Error happened')
      console.log(err)
    })
  }

  async function disconnect_mqtt(client: mqtt.MqttClient | null) {
    await client?.end()
    console.log('Disconnected')
  }

  function updateTreeWithNewNode(topic: string, message: string) {
    let parsedValue: unknown
    try {
      const parsed = JSON.parse(message)
      parsedValue = parsed.value ?? parsed
    } catch {
      parsedValue = message
    }

    const segments = topic.split('/')
    const nextData = Object.assign({}, topics)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let currentNode: any = nextData
    segments.forEach((segment: string) => {
      if (!currentNode.children) {
        currentNode.children = []
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let existingNode = currentNode.children.find((n: any) => n.name === segment)
      if (!existingNode) {
        const newNode = {
          name: segment,
          attributes: { index: 1, value: parsedValue },
        }
        currentNode.children.push(newNode)
        existingNode = newNode
      } else {
        existingNode.attributes.index = existingNode.attributes.index + 1
        existingNode.attributes.value = parsedValue
      }
      currentNode = existingNode
    })
    setTopics(nextData)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: connect/disconnect run once on mount by design, not on every render
  useEffect(() => {
    if (hivemqws) {
      connect_mqtt(hivemqws)
    }
    mpsIntervalRef.current = setInterval(() => {
      const current = messageCountRef.current
      const mps = current - lastCountRef.current
      lastCountRef.current = current
      setMessagesPerSecond(mps)
    }, 1000)
    return () => {
      disconnect_mqtt(uns_client)
      if (mpsIntervalRef.current) {
        clearInterval(mpsIntervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box p={6} h="calc(100vh - 120px)">
      <VStack align="stretch" gap={4} h="100%">
        <PageHeader
          title="Automotive - AI Bot"
          subtitle="Ask AI to extract information from the UNS"
        />

        <Card.Root flex={1} overflow="hidden" boxShadow="2xl" display="flex" flexDirection="column">
          <Card.Body overflow="auto" flex="1" minH={0}>
            <Box h="100%">
              <Chat
                introMessage={introMessage}
                promptinit={promptinit}
                topics={topics}
                questionList={questionList}
                isConnected={isConnected}
                messagesPerSecond={messagesPerSecond}
              />
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
